import { useMemo } from "react";
import { Empty, Spin, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useQuery } from "@tanstack/react-query";
import { useScopeParams } from "@/hooks/useScopeParams";
import medicalReviewApi from "@/services/medicalReviewApi";
import ReviewStatusTag, { LateArrivingTag, SaeTag } from "./ReviewStatusTag";
import { domainRank } from "./medicalReview";
import ReviewContextTimeline from "./ReviewContextTimeline";
import type { ReviewItem } from "./medicalReview.types";

/**
 * Everything under review for this participant, as a timeline over a table.
 *
 * The participant profile has a fuller timeline, but it carries every event in
 * the study; this one is filtered to the records medical review is actually
 * responsible for, which is the context a reviewer needs to judge the record in
 * front of them — what else was happening to this person that week.
 */
export default function ReviewContextTab({
  trialId: trialIdProp,
  subjectId,
  currentItemId,
  onOpenItem,
}: {
  /** Omitted on the participant profile, which is already trial-scoped. */
  trialId?: string;
  subjectId: string;
  currentItemId?: number;
  onOpenItem?: (item: ReviewItem) => void;
}) {
  const { id: scopedTrialId } = useScopeParams();
  const trialId = trialIdProp ?? scopedTrialId;

  const { data, isLoading, isError } = useQuery({
    queryKey: ["medical-review-subject", trialId, subjectId],
    queryFn: () => medicalReviewApi.subject(trialId!, subjectId),
    enabled: Boolean(trialId && subjectId),
  });

  // Same order as the timeline rows above — the table is read as its key,
  // so the two disagreeing would make the whole panel harder to follow.
  const items = useMemo(
    () =>
      [...(data?.items ?? [])].sort(
        (a, b) =>
          domainRank(a.domain) - domainRank(b.domain) ||
          a.domain.localeCompare(b.domain) ||
          (a.event_date ?? "").localeCompare(b.event_date ?? ""),
      ),
    [data],
  );

  const columns: ColumnsType<ReviewItem> = useMemo(
    () => [
      {
        title: "Type",
        dataIndex: "domain_label",
        key: "domain_label",
        width: 140,
      },
      {
        title: "Name",
        dataIndex: "summary",
        key: "summary",
        render: (summary: string) => summary || "—",
      },
      {
        title: "Date",
        dataIndex: "event_date",
        key: "event_date",
        width: 110,
        // The clinical date, not the arrival date — they are routinely months
        // apart and a reviewer is placing the event, not the ingest.
        render: (date: string | null) =>
          date ? new Date(date).toLocaleDateString() : "—",
      },
      {
        title: "Visit",
        dataIndex: "visit_label",
        key: "visit_label",
        width: 130,
        render: (visit: string) => visit || "—",
      },
      {
        title: "Review status",
        key: "status",
        width: 180,
        render: (_: unknown, row: ReviewItem) => (
          <span className="flex flex-wrap items-center gap-1">
            {row.sae_tier && <SaeTag />}
            {row.late_arriving && <LateArrivingTag />}
            <ReviewStatusTag status={row.status} />
          </span>
        ),
      },
    ],
    [],
  );

  if (isLoading) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Spin />
      </div>
    );
  }
  if (isError || !data) {
    return (
      <Empty description="Could not load this participant." className="my-10" />
    );
  }
  if (items.length === 0) {
    return (
      <Empty
        description="Nothing else is under medical review for this participant."
        className="my-10"
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <ReviewContextTimeline items={items} currentItemId={currentItemId} />
      <Table
        size="small"
        rowKey="id"
        columns={columns}
        dataSource={items}
        pagination={false}
        scroll={{ x: "max-content" }}
        rowClassName={(row) =>
          row.id === currentItemId ? "bg-[#F1F7FA]" : "cursor-pointer"
        }
        onRow={(row) => ({
          onClick: () => {
            if (row.id !== currentItemId) onOpenItem?.(row);
          },
        })}
      />
    </div>
  );
}
